import * as React from 'react';
import { I18nContextProvider } from 'i18n/I18nProvider';
import Toast from 'components/Toast';
import toastNotification, { claimToastRendering } from 'state/toast';
import { ISessionData } from 'data/schema';
import GlobalConfirmDialog from 'components/ConfirmDialog/GlobalConfirmDialog';

export type TWebComponentProps = {
  [key: string]:
    | string
    | number
    | boolean
    | undefined
    | React.ReactNode
    | object
    | HTMLElement
    | ShadowRoot
    | ((...args: unknown[]) => unknown);
};

export interface IWebComponentContext extends TWebComponentProps {
  hasSlot?: boolean;
  rootElement?: ShadowRoot;
  webComponent?: HTMLElement & TWebComponentProps;
}

export interface IWebComponentRootProps extends Partial<ISessionData>, IWebComponentContext {
  children: React.ReactNode;
}

const WebComponentContext = React.createContext<IWebComponentContext | undefined>(undefined);

export function WebComponentRoot({ children, hasSlot, ...props }: IWebComponentRootProps) {
  const toastState = toastNotification.useState();
  const [shouldRenderToast, setShouldRenderToast] = React.useState(false);

  // Only decide to render toast once when show becomes true and not being rendered
  React.useEffect(() => {
    if (toastState.show && !toastState.isBeingRendered) {
      if (claimToastRendering()) {
        setShouldRenderToast(true);
      }
    } else if (!toastState.show) {
      setShouldRenderToast(false);
    }
  }, [toastState.show, toastState.isBeingRendered]);

  return (
    <WebComponentContext.Provider value={{ hasSlot, ...props }}>
      <I18nContextProvider>
        {shouldRenderToast && <Toast />}
        {<GlobalConfirmDialog />}
        {children}
      </I18nContextProvider>
    </WebComponentContext.Provider>
  );
}

export default WebComponentRoot;

export const useWebComponent = (): IWebComponentContext => {
  const context = React.useContext(WebComponentContext);
  if (context === undefined) {
    // hook used without the WebComponentRoot parent
    return { hasSlot: false };
  }
  return context;
};
